import { useCallback, useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Grid, 
  InputLabel, 
  MenuItem, 
  Paper, 
  FormControl, 
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@material-ui/core';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import {
  extractValidationErrors,
  GetPatient,
  RegisterPatient,
  ValidationError,
  GetPrograms,
  GetPatientByCpf,
  GetPatientByRg,
  ProgramPatients,
  Program,
} from '@bforms-web/common';

import { PATIENTS_ROUTE } from '../../../constants';
import { useLoading } from '../../../contexts/loading.context';
import Button from '../../Button';
import Input from '../../Input';
import PageTitle from '../../PageTitle';

import { useStyles } from './styles';
import { IMaskInputCellPhone, IMaskInputCPF, IMaskInputDate, IMaskInputPhoneNoCodeBr, IMaskInputRG } from '../../IMaskInput';
import { format, parse, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { citiesDataList } from '../../../data/citiesData';  // Dados convertidos
import { City } from '../../../data/city';
import { PatientGender } from '../../../enums/gender';
import { KeysLocalStorage } from '../../../enums/keys-localstorage';

export type PatientRegisterProps = {
  getPatient: GetPatient;
  registerPatient: RegisterPatient;
  getPrograms: GetPrograms;
  getPatientByCpf: GetPatientByCpf;
  getPatientByRg: GetPatientByRg;
};

function removeMask(input: string): string {
  return input.replace(/\D/g, ''); // Remove tudo o que não for número
}

function calculateAge(birthDate: string): number | null {
  if (!birthDate || birthDate.length !== 10) return null; // dd/mm/yyyy
  
  try {
    const [day, month, year] = birthDate.split('/').map(Number);
    const birth = new Date(year, month - 1, day);
    const today = new Date();
    
    let age = today.getFullYear() - birth.getFullYear();
    const monthDifference = today.getMonth() - birth.getMonth();
    
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  } catch {
    return null;
  }
}

export default function PatientRegister({ getPatient, registerPatient, getPrograms, getPatientByCpf, getPatientByRg }: PatientRegisterProps): JSX.Element {
  const classes = useStyles();
  const formRef = useRef({} as any);
  const { isLoading, showLoading, hideLoading } = useLoading();
  const { push } = useHistory();
  const { id } = useParams<{ id: string }>();
  
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState('');

  const [selectedState, setSelectedState] = useState<string>('');
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>('');

  const [isFieldsEnabled, setIsFieldsEnabled] = useState(false);
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');

  const [isProgramDisabled, setIsProgramDisabled] = useState(false);
  const [ageAlert, setAgeAlert] = useState(false);
  const [showAgeConfirmDialog, setShowAgeConfirmDialog] = useState(false);
  const [calculatedAge, setCalculatedAge] = useState<number | null>(null);
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  const [selectedGender, setGender] = useState<PatientGender | ''>('');
  const handleGenderChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setGender(event.target.value as PatientGender);
  };

  const handleSubmit = async (data: any) => {
    if (ageAlert && data.birthDate) {
      const age = calculateAge(data.birthDate);
      if (age !== null && age < 40) {
        setPendingFormData(data);
        setCalculatedAge(age);
        setShowAgeConfirmDialog(true);
        return;
      }
    }

    // Prosseguir com o registro normal
    await processFormSubmission(data);
  };

  const processFormSubmission = async (data: any) => {
    showLoading();
    async function runAsync() {
        try {
            formRef.current.setErrors({});
          
            const formattedData = {
              ...data,
              cpf: data.cpf && data.cpf.trim() !== '' ? removeMask(data.cpf) : null,
              rg: data.rg && data.rg.trim() !== '' ? removeMask(data.rg) : null,
              email: data.email && data.email !== '' ? data.email  : null,
              cellPhone: data.cellPhone && data.cellPhone.trim() !== '' ? removeMask(data.cellPhone) : null,
              homePhone: data.homePhone && data.homePhone.trim() !== '' ? removeMask(data.homePhone) : null,
              additionalPhone: data.additionalPhone && data.additionalPhone.trim() !== '' ? removeMask(data.additionalPhone) : null,
              programId: selectedProgram,
              uf: selectedState && selectedState.trim() !== '' ? selectedState : null,
              city: selectedCity && selectedCity.trim() !== '' ? selectedCity : null,
              gender: selectedGender && selectedGender.trim() !== '' ? selectedGender : null,
              birthDate: data.birthDate && data.birthDate.trim() !== '' ? data.birthDate : null,
            };
          
            if (formattedData.birthDate) {
                const [day, month, year] = formattedData.birthDate.split('/').map(Number);
                const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
                formattedData.birthDate = format(date, 'yyyy-MM-dd HH:mm:ss');
            }

            // Validação manual para CPF e RG
            const errors: { cpf?: string, rg?: string } = {};
            if (!formattedData.cpf && !formattedData.rg) {
                errors.cpf = 'CPF ou RG deve ser preenchido';
                errors.rg = 'CPF ou RG deve ser preenchido';
            } else {
                if (formattedData.cpf && formattedData.cpf.length !== 11) {
                    errors.cpf = 'CPF deve ter 11 dígitos';
                }
                if (formattedData.rg && formattedData.rg.length !== 9) {
                    errors.rg = 'RG deve ter 9 dígitos';
                }
            }

            if (Object.keys(errors).length > 0) {
                toast.error('CPF ou RG deve ser preenchido');
                throw new ValidationError(Object.values(errors), '', '');
            }

            await registerPatient.execute({
                id,
                ...formattedData,
            });
          
            localStorage.removeItem(KeysLocalStorage.DATA_PATIENT);

            toast.success('Paciente registrado com sucesso');
            push(PATIENTS_ROUTE);
          } catch (err: any) {
            console.log(err);
            if (err instanceof ValidationError) {
              const errorMessages = err.errors;
              if (errorMessages.includes('CPF ou RG deve ser preenchido')) {
                toast.error('CPF ou RG deve ser preenchido');
              } else {
                const errors = extractValidationErrors(err);
                formRef.current.setErrors(errors);
                // Exibir o primeiro erro como toast
                if (errorMessages.length > 0) {
                  toast.error(errorMessages[0]);
                }
              }
            } else {
              toast.error(err.message || 'Ocorreu um erro ao processar o formulário');
            }
          } finally {
            hideLoading();
          }
    }
    await runAsync();
};

  useEffect(() => {
    showLoading();
    async function runAsync() {
      try {
        const programsResponse = await getPrograms.execute();  
        setPrograms(programsResponse);
      } catch (err) {
        console.log(err);
        toast.error('Falha ao carregar programas');
      } finally {
        hideLoading();
      }
    }

    runAsync();
  }, [getPrograms]);

  useEffect(() => {
    if (!id) return;

    showLoading();
    async function runAsync() {
      try {
        const programPatient: ProgramPatients = JSON.parse(localStorage.getItem(KeysLocalStorage.DATA_PATIENT) ?? '{}');
        let patient = programPatient.patient;

        if (patient.birthDate) {
          const date = new Date(patient.birthDate);
          const day = String(date.getUTCDate()).padStart(2, '0');
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const year = date.getUTCFullYear();
          const formattedBirthDate = `${day}/${month}/${year}`;
          
          // Verificar idade quando os dados do paciente são carregados no modo de edição
          const age = calculateAge(formattedBirthDate);
          setAgeAlert(age !== null && age < 40);
          
          // Atualizar o patient com a data formatada
          patient = { ...patient, birthDate: formattedBirthDate };
        }

        formRef.current.setData(patient);
        if (programPatient.program.id) {
          setSelectedProgram(programPatient.program.id);
        }

        if (patient.uf) {
          setSelectedState(patient.uf);

          const stateObj = citiesDataList.find((s) => s.value === patient.uf);
          if (stateObj) {
            setAvailableCities(stateObj.cities);
          }
        }
        
        if (patient.city) {
          setSelectedCity(patient.city);
        }

        if (patient.gender) {
          setGender(patient.gender);
        }

        setIsProgramDisabled(true);
      } catch (err) {
        console.log(err);
        toast.error('Falha ao carregar paciente');
        push(PATIENTS_ROUTE);
      } finally {
        hideLoading();
      }
    }

    runAsync();
  }, [id, getPatient, push]);

  const handleProgramChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedValue = event.target.value;
    setSelectedProgram(selectedValue as string);
  };

  const handleStateChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const state = event.target.value as string;
    setSelectedState(state);
    setSelectedCity(''); 

    const stateObj = citiesDataList.find((s) => s.value === state);
    if (stateObj) {
      setAvailableCities(stateObj.cities);
    }
  };

  const handleCityChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedCity(event.target.value as string);
  };

  const onChangeCpfInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCpf(event.target.value);
  }, []);
  
  const onChangeRgInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRg(event.target.value);
  }, []);

  const onChangeBirthDate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const birthDate = event.target.value;
    const age = calculateAge(birthDate);
    setAgeAlert(age !== null && age < 40);
  }, []);

  // Funções para tratar as ações do pop-up de confirmação de idade
  const handleAgeConfirmationProceed = async () => {
    setShowAgeConfirmDialog(false);
    if (pendingFormData) {
      await processFormSubmission(pendingFormData);
      setPendingFormData(null);
      setCalculatedAge(null);
    }
  };

  const handleAgeConfirmationCancel = () => {
    setShowAgeConfirmDialog(false);
    setPendingFormData(null);
    setCalculatedAge(null);
    
    // Focar no campo de data de nascimento para correção
    setTimeout(() => {
      const birthDateField = document.querySelector('input[name="birthDate"]') as HTMLInputElement;
      if (birthDateField) {
        birthDateField.focus();
        birthDateField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };
  
  useEffect(() => {
    if (cpf && cpf.length === 14) {
      const cleanCpf = removeMask(cpf);
      getPatientByCpf.execute({ cpf: cleanCpf }).then(handlePatientData);
    }
  }, [cpf]);
  
  useEffect(() => {
    if (rg && rg.length === 12) {
      const cleanRg = removeMask(rg);
      getPatientByRg.execute({ rg: cleanRg }).then(handlePatientData);
    }
  }, [rg]);

  // Validação da idade em tempo real
  useEffect(() => {
    const birthDateField = formRef.current?.getFieldValue?.('birthDate');
    if (birthDateField) {
      const age = calculateAge(birthDateField);
      setAgeAlert(age !== null && age < 40);
    } else {
      setAgeAlert(false);
    }
  }, []); // Será executado por meio de onChange no campo

// Função auxiliar para tratar os dados do paciente
const handlePatientData = (patient: any) => {
    if (patient) {
        let updatedPatient = { ...patient };
        
        if (patient.birthDate) {
            const formattedBirthDate = format(new Date(patient.birthDate), 'dd/MM/yyyy');
            updatedPatient.birthDate = formattedBirthDate;
            // Verificar idade quando os dados do paciente são carregados
            const age = calculateAge(formattedBirthDate);
            setAgeAlert(age !== null && age < 40);
        }

        formRef.current.setData(updatedPatient);

        if (patient.uf) {
            setSelectedState(patient.uf);
            const stateObj = citiesDataList.find((s) => s.value === patient.uf);
            if (stateObj) {
                setAvailableCities(stateObj.cities);
            }
        }

        if (patient.city) {
            setSelectedCity(patient.city);
        }

        if (patient.gender) {
            setGender(patient.gender);
        }

        setIsFieldsEnabled(true);
        setIsProgramDisabled(false);
    } else {
        formRef.current.setData({
            cpf: cpf,
            rg: rg,
            name: '',
            email: '',
            birthDate: '',
            cellPhone: '',
            homePhone: '',
            additionalPhone: '',
        });

        setGender('');
        setSelectedState('');
        setSelectedCity('');
        setAgeAlert(false); // Resetar o alerta de idade

        setIsFieldsEnabled(true);
        setIsProgramDisabled(false);
    }
};

  return (
    <Box className={classes.container}>
      <PageTitle>{!id ? 'Cadastro de paciente' : 'Editar paciente'}</PageTitle>
      <br />
      <Form ref={formRef} onSubmit={handleSubmit}>
        <Paper className={classes.container}>
          <Grid container spacing={2} direction="row">
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Programa</InputLabel>
                  <Select
                    label="Programa"
                    name="programId"
                    onChange={handleProgramChange}
                    disabled={isLoading || isProgramDisabled}
                    value={selectedProgram}
                    required={true}
                  >
                    {programs.map((program) => (
                      <MenuItem key={program.id} value={program.id}>
                        {program.name}
                      </MenuItem>
                    ))}
                  </Select>
              </FormControl>
            </Grid>
           <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="cpf"
                variant="outlined"
                label={'CPF'}
                className={classes.input}
                disabled={isLoading}
                mask={IMaskInputCPF}
                value={cpf}
                onChange={onChangeCpfInput}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="rg"
                variant="outlined"
                label={'RG'}
                className={classes.input}
                disabled={isLoading}
                mask={IMaskInputRG}
                value={rg}
                onChange={onChangeRgInput}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="name"
                variant="outlined"
                label={'Nome'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="email"
                variant="outlined"
                label={'Email'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="birthDate"
                variant="outlined"
                label={'Data de Nascimento'}
                className={ageAlert ? classes.inputAlert : classes.input}
                disabled={!isFieldsEnabled}
                mask={IMaskInputDate}
                onChange={onChangeBirthDate}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Gênero</InputLabel>
                <Select
                  label="Gênero"
                  name="gender"
                  value={selectedGender}
                  onChange={handleGenderChange}
                  disabled={!isFieldsEnabled}
                >
                  {Object.keys(PatientGender).map((key) => {
                    const genderKey = key as keyof typeof PatientGender;
                    const genderValue = PatientGender[genderKey];

                    return (
                      <MenuItem key={genderValue} value={genderValue}>
                        {genderValue}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>UF</InputLabel>
                <Select
                  label="UF"
                  value={selectedState}
                  onChange={handleStateChange}
                  disabled={!isFieldsEnabled}
                >
                  {citiesDataList.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Cidade</InputLabel>
                <Select
                  label="Cidade"
                  value={selectedCity}
                  onChange={handleCityChange}
                  disabled={!selectedState}
                >
                  {availableCities.map((city) => (
                    <MenuItem key={city.value} value={city.value}>
                      {city.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="cellPhone"
                variant="outlined"
                label={'Celular'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                mask={IMaskInputCellPhone}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="homePhone"
                variant="outlined"
                label={'Telefone Residencial'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                mask={IMaskInputPhoneNoCodeBr}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="additionalPhone"
                variant="outlined"
                label={'Telefone Adicional'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                mask={IMaskInputCellPhone}
              />
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <Button type="submit" variant="contained" color="primary">
                {'Salvar'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Form>

      {/* Dialog de confirmação para idade menor que 40 anos */}
      <Dialog
        open={showAgeConfirmDialog}
        onClose={() => {}} // Impede fechar clicando fora
        maxWidth="sm"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 'bold', color: '#f57c00' }}>
            ⚠️ ATENÇÃO
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: '16px' }}>
            A idade do paciente ({calculatedAge} anos) é inferior à idade mínima recomendada/estabelecida de 40 anos.
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
            Deseja prosseguir com o registro?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button
            onClick={handleAgeConfirmationCancel}
            variant="outlined"
            color="secondary"
            style={{ marginRight: '8px' }}
          >
            Corrigir Data / Voltar
          </Button>
          <Button
            onClick={handleAgeConfirmationProceed}
            variant="contained"
            color="primary"
            autoFocus
          >
            Prosseguir / Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );  
}
